// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState } from 'react';
import { MoreVert } from '@mui/icons-material';
import { Box } from '@mui/material';
import { GridColDef, GridRowParams, GridToolbarContainer } from '@mui/x-data-grid';
import { RegistrarService } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Registrar';
import { InstrumentConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/Instrument';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { SyncButton } from '../../../components/Button/SyncButton';
import { ChoiceDialog } from '../../../components/ChoiceDialog';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { useExerciseSubmission } from '../../../hooks/mutations/ledger/useSubmission';
import useArchiveInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useArchiveInstrumentConfiguration';
import useRegistryInstrumentConfigurations from '../../../hooks/queries/registry/configuration/useRegistryInstrumentConfigurations';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import useAllocationFactories from '../../../hooks/queries/registry/tokenStandard/useAllocationFactories';
import useTransferRules from '../../../hooks/queries/registry/transfer/useTransferRules';
import useIsPackageAvailableFrom from '../../../hooks/queries/settings/useIsPackageAvailableFrom';
import { useOperatorParty } from '../../../hooks/queries/utility-operator/useOperatorParty';
import RequiredCredentials from '../../../modules/Credential/RequiredCredentials';
import InstrumentIdentifiers from '../../../modules/Instrument/InstrumentIdentifiers';
import TemplateContract from '../../../utils/TemplateContract';
import {
  SUC_CREATE_ALLOCATION_FACTORY,
  ERR_CREATE_ALLOCATION_FACTORY,
  SUC_CREATE_TRANSFER_RULE,
  ERR_CREATE_TRANSFER_RULE,
} from '../../../utils/strings';
import { PageWrapper } from '../../PageWrapper';
import CreateInstrumentConfigurationDialog from './CreateInstrumentConfigurationDialog';
import { InstrumentConfigurationDetails } from './InstrumentConfigurationDetails';
import { useCreateInstrumentConfigurationActions } from './useCreateInstrumentConfigurationActions';

type Row = TemplateContract<InstrumentConfiguration>;

const createHeaders: () => GridColDef<Row>[] = () => {
  return [
    {
      field: 'registrar',
      headerName: 'Registrar',
      renderCell: (params) => <PartyDisplay party={params.row.payload.registrar} />,
      flex: 150,
      valueGetter: (params) => params.row.payload.registrar,
      filterable: true,
    },
    {
      field: 'instrumentId',
      headerName: 'InstrumentId',
      valueGetter: (params) => params.row.payload.defaultIdentifier.id,
      flex: 150,
      filterable: true,
    },
    {
      field: 'additionalIdentifiers',
      headerName: 'Additional Instrument Identifiers',
      renderCell: (params) => (
        <InstrumentIdentifiers instrumentIdentifiers={params.row.payload.additionalIdentifiers} />
      ),
      flex: 250,
      filterable: false,
    },
    {
      field: 'issuerRequirements',
      headerName: 'Required Credentials for Issuers',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.issuerRequirements} />
      ),
      flex: 250,
      filterable: false,
    },
    {
      field: 'holderRequirement',
      headerName: 'Required Credentials for Holders',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.holderRequirements} />
      ),
      flex: 250,
      filterable: false,
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      align: 'right',
      renderCell: () => (
        <Box sx={{ cursor: 'pointer', display: 'flex' }}>
          <MoreVert />
        </Box>
      ),
      filterable: false,
    },
  ];
};

const Toolbar = () => {
  const [createInstrumentConfigurationIsOpen, setCreateInstrumentConfigurationIsOpen] =
    useState(false);
  const registrarService = useRegistrarService();
  const { submit: createAllocationFactory } = useExerciseSubmission(
    RegistrarService.RegistrarService_CreateAllocationFactory,
    SUC_CREATE_ALLOCATION_FACTORY,
    ERR_CREATE_ALLOCATION_FACTORY,
  );
  const { submit: createTransferRule } = useExerciseSubmission(
    RegistrarService.RegistrarService_CreateTransferRule,
    SUC_CREATE_TRANSFER_RULE,
    ERR_CREATE_TRANSFER_RULE,
  );
  const operator = useOperatorParty()?.data || '';
  const party = useModuleParty();

  const allocationFactories = useAllocationFactories(operator, party);
  const transferRules = useTransferRules(operator, party);

  const isPackageAvailable = useIsPackageAvailableFrom(
    'utility-registry-app-v0',
    [operator, party],
    '0.3.0',
  );

  const isLoading =
    registrarService.isLoading &&
    allocationFactories.isLoading &&
    transferRules.isLoading &&
    isPackageAvailable.isLoading;

  return (
    <GridToolbarContainer>
      {isLoading ? null : (
        <>
          {createInstrumentConfigurationIsOpen && (
            <CreateInstrumentConfigurationDialog
              open={createInstrumentConfigurationIsOpen}
              onClose={() => setCreateInstrumentConfigurationIsOpen(false)}
            />
          )}
          <SyncButton
            onClick={() => setCreateInstrumentConfigurationIsOpen(true)}
            disabled={!registrarService.data}
          >
            Create instrument configuration
          </SyncButton>
          {operator && registrarService.data && (
            <AsyncButton
              onClick={async () => {
                return createAllocationFactory({
                  cid: registrarService.data!.contractId,
                  argument: {},
                }).catch(console.error);
              }}
              disabled={
                !registrarService.data ||
                (allocationFactories.data?.length !== undefined &&
                  allocationFactories.data?.length > 0)
              }
            >
              Create Allocation Factory
            </AsyncButton>
          )}
          {operator && registrarService.data && isPackageAvailable.data && (
            <AsyncButton
              onClick={async () => {
                return createTransferRule({
                  cid: registrarService.data!.contractId,
                  argument: {},
                }).catch(console.error);
              }}
              disabled={
                !registrarService.data ||
                (transferRules.data?.length !== undefined && transferRules.data?.length > 0)
              }
            >
              Create Transfer Rule
            </AsyncButton>
          )}
        </>
      )}
    </GridToolbarContainer>
  );
};

export const InstrumentConfigurations = () => {
  const configurations = useRegistryInstrumentConfigurations();
  const registrarService = useRegistrarService();
  const archiveInstrumentConfiguration = useArchiveInstrumentConfiguration();

  const isLoading =
    configurations.isLoading ||
    registrarService.isLoading ||
    archiveInstrumentConfiguration.isLoading;
  const [selectedRow, setSelectedRow] = useState<Row | undefined>(undefined);
  const onRowClick = (r: GridRowParams<Row>) => {
    setSelectedRow(r.row);
  };
  const onClose = () => {
    setSelectedRow(undefined);
  };
  const actions = useCreateInstrumentConfigurationActions(onClose, selectedRow);
  if (isLoading) return <Loading />;
  return (
    <PageWrapper pageName="">
      <ChoiceDialog
        open={!!selectedRow}
        onClose={onClose}
        actions={actions}
        title="Instrument Configuration"
      >
        {selectedRow && <InstrumentConfigurationDetails row={selectedRow} />}
      </ChoiceDialog>

      <DataTable
        onRowClick={onRowClick}
        rows={configurations.data || []}
        columns={createHeaders()}
        rowKey={(a) => a.contractId}
        toolbar={Toolbar}
      />
    </PageWrapper>
  );
};
